<script setup lang="ts">
import type { Component } from "vue";
import type { IconData } from "@/generated/models";
import { hashKey } from "@tanstack/vue-query";
import { AvatarFallback, AvatarRoot } from "reka-ui";
import { computed } from "vue";
import { IconType } from "@/generated/models";
import { IconSet, themes } from "./curatedIcons";
import NodeIconUploaded from "./NodeIconUploaded.vue";

const props = defineProps<{
  variant: "outline" | "boxed";
  letter: string;
  shortcut?: string;
  icon?: IconData;
  withoutBackground?: boolean;
}>();

// need a key to ensure the component re-renders when the icon changes
// this is necessary because Avatar component won't re-render if switching between image and fallback
const key = computed(() => {
  return hashKey([
    "NodeIcon",
    props.shortcut,
    props.icon,
  ]);
});

type IconToRender
  = | { type: "emoji"; native: string }
    | { type: "icon"; component: Component; bgColor: string; iconColor: string }
    | { type: "upload"; imageHash: string; shortcut: string };

const iconToRender = computed<IconToRender | undefined>(() => {
  if (!props.icon) return undefined;

  if (props.icon.Type === IconType.Emoji && props.icon.Native) {
    const native = props.icon.Native;
    if (!native) return undefined;
    return {
      type: "emoji",
      native,
    };
  }

  if (props.icon.Type === IconType.Icon && props.icon.IconId) {
    const id = props.icon.IconId;
    const iconComponent = IconSet.find(icon => icon.id === id);
    if (!iconComponent) return undefined;
    return {
      type: "icon",
      component: iconComponent.component,
      bgColor: props.icon.BgColor ?? themes[0].bgColor,
      iconColor: props.icon.IconColor ?? themes[0].iconColor,
    };
  }

  if (props.icon.Type === IconType.Upload && props.shortcut && props.icon.ImageHash) {
    return {
      type: "upload",
      imageHash: props.icon.ImageHash,
      shortcut: props.shortcut,
    };
  }

  return undefined;
});
</script>

<template>
  <AvatarRoot :key="key" as="div" class="flex align-middle text-white">
    <NodeIconUploaded
      v-if="iconToRender && iconToRender.type === 'upload'"
      :image-hash="iconToRender.imageHash"
      :shortcut="iconToRender.shortcut"
      :without-background="withoutBackground"
      class="size-full"
    />

    <AvatarFallback
      v-if="iconToRender && iconToRender.type === 'emoji'"
      class="inline-flex size-full flex-none items-center justify-center text-center leading-none"
    >
      {{ iconToRender.native }}
    </AvatarFallback>

    <AvatarFallback
      v-else-if="iconToRender && iconToRender.type === 'icon'"
      class="inline-flex size-full flex-none items-center justify-center place-self-center rounded-sm"
      :class="{
        'p-0.5': props.variant === 'boxed',
      }"
      :style="{
        backgroundColor: props.variant === 'boxed' ? iconToRender.bgColor : undefined,
      }"
    >
      <component
        :is="iconToRender.component"
        class="size-full"
        :style="{
          color: iconToRender.iconColor,
        }"
        aria-hidden="true"
      />
    </AvatarFallback>

    <AvatarFallback
      v-else
      class="flex size-full items-center justify-center overflow-hidden rounded-sm leading-none font-semibold text-slate-500 uppercase select-none"
      :class="{
        'bg-gray-200': !withoutBackground,
      }"
      aria-hidden="true"
    >
      {{ letter }}
    </AvatarFallback>
  </AvatarRoot>
</template>
